import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import './SuggestionsPage.css';
import { localeFor } from '../utils/timeFormat';

const SECTIONS = ['rmc', 'events', 'teams', 'users', 'results', 'other'];

const SuggestionsPage = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();

  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);

  const [formOpen, setFormOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [section, setSection] = useState('');
  const [titleError, setTitleError] = useState('');
  const [bodyError, setBodyError] = useState('');
  const [sectionError, setSectionError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const [votingId, setVotingId] = useState(null);
  const [statusUpdating, setStatusUpdating] = useState(null);

  useEffect(() => {
    fetchSuggestions();
  }, []);

  const fetchSuggestions = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/suggestions');
      setSuggestions(res.data);
    } catch (err) {
      console.error('Failed to fetch suggestions', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let valid = true;
    if (!title.trim()) { setTitleError(t('suggestions.titleRequired')); valid = false; }
    if (!body.trim()) { setBodyError(t('suggestions.bodyRequired')); valid = false; }
    if (!section) { setSectionError(t('suggestions.sectionRequired')); valid = false; }
    if (!valid) return;

    setSubmitting(true);
    try {
      await axios.post('/api/suggestions', { title: title.trim(), body: body.trim(), section });
      setTitle('');
      setBody('');
      setSection('');
      setFormOpen(false);
      setSubmitSuccess(true);
      setTimeout(() => setSubmitSuccess(false), 3000);
      fetchSuggestions();
    } catch (err) {
      console.error('Failed to submit suggestion', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleVote = async (suggestion, voteType) => {
    if (votingId) return;
    setVotingId(suggestion.id);
    try {
      let res;
      if (suggestion.current_user_vote === voteType) {
        res = await axios.delete(`/api/suggestions/${suggestion.id}/vote`);
      } else {
        res = await axios.post(`/api/suggestions/${suggestion.id}/vote`, { vote_type: voteType });
      }
      setSuggestions(prev => prev.map(s =>
        s.id === suggestion.id
          ? { ...s, up_votes: res.data.up_votes, down_votes: res.data.down_votes, current_user_vote: res.data.current_user_vote }
          : s
      ));
    } catch (err) {
      console.error('Vote failed', err);
    } finally {
      setVotingId(null);
    }
  };

  const handleStatusChange = async (suggestion, status) => {
    setStatusUpdating(suggestion.id);
    try {
      await axios.patch(`/api/suggestions/${suggestion.id}/status`, { status });
      setSuggestions(prev => prev.map(s =>
        s.id === suggestion.id ? { ...s, status } : s
      ));
    } catch (err) {
      console.error('Status update failed', err);
    } finally {
      setStatusUpdating(null);
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    if (language === 'ru') {
      return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }
    return d.toLocaleDateString(localeFor(language, 'en-GB'), { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const sectionLabel = (sec) => {
    const key = `suggestions.section${sec.charAt(0).toUpperCase() + sec.slice(1)}`;
    return t(key) || sec;
  };

  const statusLabel = (status) => {
    switch (status) {
      case 'pending': return t('suggestions.statusNew');
      case 'rejected': return t('suggestions.statusRejected');
      case 'implemented': return t('suggestions.statusImplemented');
      default: return status;
    }
  };

  const sorted = [...suggestions].sort((a, b) => {
    const netA = a.up_votes - a.down_votes;
    const netB = b.up_votes - b.down_votes;
    if (netB !== netA) return netB - netA;
    return new Date(b.created_at) - new Date(a.created_at);
  });

  return (
    <div className="suggestions-page">
      <div className="suggestions-header">
        <h2>{t('suggestions.title')}</h2>
        <p className="suggestions-subtitle">{t('suggestions.pageSubtitle')}</p>
      </div>

      {user && (
        <div className="suggestions-form-area">
          {submitSuccess && (
            <div className="suggestions-success">{t('suggestions.submitSuccess')}</div>
          )}
          {!formOpen ? (
            <button className="suggestions-add-btn" onClick={() => setFormOpen(true)}>
              + {t('suggestions.newSuggestion')}
            </button>
          ) : (
            <form className="suggestions-form" onSubmit={handleSubmit}>
              <div className="suggestions-form-group">
                <label>{t('suggestions.sectionLabel')}</label>
                <select
                  value={section}
                  onChange={e => { setSection(e.target.value); setSectionError(''); }}
                  className={sectionError ? 'input-error' : ''}
                >
                  <option value="">—</option>
                  {SECTIONS.map(sec => (
                    <option key={sec} value={sec}>{sectionLabel(sec)}</option>
                  ))}
                </select>
                {sectionError && <span className="field-error">{sectionError}</span>}
              </div>
              <div className="suggestions-form-group">
                <label>{t('suggestions.titleLabel')}</label>
                <input
                  type="text"
                  value={title}
                  onChange={e => { setTitle(e.target.value); setTitleError(''); }}
                  placeholder={t('suggestions.titlePlaceholder')}
                  maxLength={200}
                  className={titleError ? 'input-error' : ''}
                />
                {titleError && <span className="field-error">{titleError}</span>}
              </div>
              <div className="suggestions-form-group">
                <label>{t('suggestions.bodyLabel')}</label>
                <textarea
                  value={body}
                  onChange={e => { setBody(e.target.value); setBodyError(''); }}
                  placeholder={t('suggestions.bodyPlaceholder')}
                  maxLength={2000}
                  rows={4}
                  className={bodyError ? 'input-error' : ''}
                />
                {bodyError && <span className="field-error">{bodyError}</span>}
              </div>
              <div className="suggestions-form-actions">
                <button type="button" className="btn-cancel" onClick={() => { setFormOpen(false); setTitle(''); setBody(''); setSection(''); setTitleError(''); setBodyError(''); setSectionError(''); }}>
                  {t('common.cancel')}
                </button>
                <button type="submit" className="btn-submit" disabled={submitting}>
                  {submitting ? t('suggestions.submitting') : t('suggestions.submit')}
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      <div className="suggestions-list">
        {loading ? (
          <div className="suggestions-loading">...</div>
        ) : sorted.length === 0 ? (
          <div className="suggestions-empty">{t('suggestions.noSuggestions')}</div>
        ) : (
          sorted.map(s => (
            <div key={s.id} className={`suggestion-card suggestion-status-${s.status}`}>
              <div className="suggestion-meta">
                <span className={`suggestion-status-badge status-${s.status}`}>
                  {statusLabel(s.status)}
                </span>
                <span className="suggestion-section-badge">
                  {sectionLabel(s.section)}
                </span>
                <span className="suggestion-author">
                  {t('suggestions.by')} @{s.user.nickname}
                </span>
                <span className="suggestion-date">{formatDate(s.created_at)}</span>
              </div>
              <h3 className="suggestion-title">{s.title}</h3>
              <p className="suggestion-body">{s.body}</p>

              <div className="suggestion-footer">
                <div className="suggestion-votes">
                  <button
                    className={`vote-btn vote-up${s.current_user_vote === 'up' ? ' voted' : ''}`}
                    onClick={() => handleVote(s, 'up')}
                    disabled={votingId === s.id || s.status !== 'pending'}
                    title={t('suggestions.voteUp')}
                  >
                    👍 <span>{s.up_votes}</span>
                  </button>
                  <button
                    className={`vote-btn vote-down${s.current_user_vote === 'down' ? ' voted' : ''}`}
                    onClick={() => handleVote(s, 'down')}
                    disabled={votingId === s.id || s.status !== 'pending'}
                    title={t('suggestions.voteDown')}
                  >
                    👎 <span>{s.down_votes}</span>
                  </button>
                </div>

                {user?.admin && (
                  <div className="suggestion-admin-actions">
                    <button
                      className={`btn-status btn-status-implemented${s.status === 'implemented' ? ' active' : ''}`}
                      onClick={() => handleStatusChange(s, 'implemented')}
                      disabled={statusUpdating === s.id || s.status === 'implemented'}
                    >
                      {t('suggestions.statusImplemented')}
                    </button>
                    <button
                      className={`btn-status btn-status-rejected${s.status === 'rejected' ? ' active' : ''}`}
                      onClick={() => handleStatusChange(s, 'rejected')}
                      disabled={statusUpdating === s.id || s.status === 'rejected'}
                    >
                      {t('suggestions.statusRejected')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default SuggestionsPage;
